/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useEffect, useState } from "react";
import {
  EventType,
  MsgType,
  RoomEvent,
  MatrixEventEvent,
  type MatrixClient,
} from "matrix-js-sdk";
import { Button } from "@vector-im/compound-web";
import { Modal } from "../Modal";
import panel from "./VocemModeration.module.css";
import styles from "./VocemChat.module.css";

export interface ChatMessage {
  id: string;
  sender: string;
  body: string;
}
export interface ChatViewProps {
  open: boolean;
  messages: ChatMessage[];
  text: string;
  busy: boolean;
  error: string;
  onOpen: () => void;
  onClose: () => void;
  onText: (value: string) => void;
  onSend: () => void;
}
export function VocemChatView(props: ChatViewProps) {
  return (
    <>
      <Button size="md" kind="secondary" onClick={props.onOpen}>
        Chat de la sesión
      </Button>
      <Modal
        title="Chat de la sesión"
        open={props.open}
        onDismiss={props.onClose}
        hideHeader
      >
        <div className={`${panel.panel} ${styles.panel}`}>
          <header className={panel.heading}>
            <div>
              <h2>Chat de la sesión</h2>
              <span className={panel.subtitle}>Conversación del grupo</span>
            </div>
            <button
              className={panel.close}
              onClick={props.onClose}
              aria-label="Cerrar"
            >
              ×
            </button>
          </header>

          <div
            role="log"
            aria-label="Mensajes del grupo"
            aria-live="polite"
            className={styles.messages}
          >
            {props.messages.length === 0 && (
              <div className={styles.empty}>
                <strong>La conversación empieza aquí</strong>
                <p>Aún no hay mensajes disponibles.</p>
              </div>
            )}
            {props.messages.map((m) => (
              <div className={styles.message} key={m.id}>
                <span className={panel.avatar} aria-hidden="true">
                  {m.sender.replace(/^@/, "").slice(0, 1).toUpperCase()}
                </span>
                <div className={styles.bubble}>
                  <strong>
                    {m.sender.startsWith("@")
                      ? m.sender.slice(1).split(":")[0]
                      : m.sender}
                  </strong>
                  <p>{m.body}</p>
                </div>
              </div>
            ))}
          </div>
          {props.error && (
            <p className={panel.notice} role="alert">
              {props.error}
            </p>
          )}
          <form
            className={styles.composer}
            onSubmit={(e) => {
              e.preventDefault();
              props.onSend();
            }}
          >
            <label htmlFor="vocem-chat-message">Mensaje</label>
            <textarea
              id="vocem-chat-message"
              value={props.text}
              maxLength={4000}
              rows={2}
              placeholder="Escribe un mensaje…"
              className={styles.input}
              onChange={(e) => props.onText(e.target.value)}
            />
            <div className={styles.sendRow}>
              <span className={panel.subtitle}>
                Los mensajes quedan en el grupo.
              </span>
              <Button
                type="submit"
                size="md"
                disabled={props.busy || !props.text.trim()}
              >
                Enviar
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
export function VocemChat({
  client,
  roomId,
}: {
  client: MatrixClient;
  roomId: string;
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const room = client.getRoom(roomId);
    const refresh = () =>
      setMessages(
        (room?.getLiveTimeline().getEvents() ?? [])
          .filter(
            (e) =>
              e.getType() === EventType.RoomMessage &&
              typeof e.getContent().body === "string",
          )
          .slice(-50)
          .map((e) => ({
            id: e.getId() ?? String(e.getTs()),
            sender:
              room?.getMember(e.getSender() ?? "")?.name ??
              e.getSender() ??
              "Participante",
            body: e.getContent().body,
          })),
      );
    refresh();
    room?.on(RoomEvent.Timeline, refresh);
    client.on(MatrixEventEvent.Decrypted, refresh);
    return () => {
      room?.off(RoomEvent.Timeline, refresh);
      client.off(MatrixEventEvent.Decrypted, refresh);
    };
  }, [client, roomId]);
  const send = async () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    setError("");
    try {
      await client.sendMessage(roomId, {
        msgtype: MsgType.Text,
        body: text.trim(),
      });
      setText("");
    } catch {
      setError(
        "No se pudo enviar el mensaje. Revisa los permisos de la sala o inténtalo de nuevo.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <VocemChatView
      open={open}
      messages={messages}
      text={text}
      busy={busy}
      error={error}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onText={setText}
      onSend={() => void send()}
    />
  );
}
